import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute } from "@tanstack/react-router";

import { SituationPage } from "@/components/situation-page";
import { FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "Do I need a solicitor to buy a house in Queensland?",
    a: "It isn't compulsory, but almost every buyer uses a solicitor or conveyancer. The contract is binding the moment both sides sign, and the cooling-off period is short, so having someone check it and manage the deadlines protects you from expensive mistakes.",
  },
  {
    q: "What's the difference between a buyer's solicitor and a conveyancer?",
    a: "Both can handle the paperwork and settlement. A solicitor can also give legal advice when something goes wrong: a finance delay, a bad building report, a dispute over a special condition, or a seller who won't settle. At Coastside Law your file is run by a solicitor from start to finish, not passed to an assistant.",
  },
  {
    q: "When should I contact a buyer's solicitor?",
    a: "Ideally before you sign. We'll review the contract free and point out anything unusual, such as short finance dates, missing conditions or odd special conditions. If you've already signed, call us straight away so we can use the cooling-off period well.",
  },
  {
    q: "What does a buyer's solicitor actually do?",
    a: "Reviews the contract, diarises your finance and building and pest dates, orders searches, checks the title, handles transfer duty forms, reads the seller's disclosure, and settles electronically through PEXA. We also explain each step so you know where things stand.",
  },
  {
    q: "What does it cost?",
    a: "We charge a fixed fee with the searches and disbursements set out up front, so there are no surprises at settlement. See our fees page for the current figures.",
  },
  {
    q: "Do I have to be local to use you?",
    a: "No. We have offices in Golden Beach and Scarborough, and we act for buyers anywhere in Queensland. Most documents are signed electronically.",
  },
  {
    q: "What should I check on a title search?",
    a: "Check the registered owner, lot and plan, ownership type, and registered mortgages, caveats, easements and other encumbrances. An easement's terms matter as well as its position on the plan. A title search does not replace council, building approval or body corporate enquiries.",
  },
  {
    q: "Does seller disclosure replace my own due diligence?",
    a: "No. Read the Form 2 and its attachments before signing, then check the matters important to your purchase: approvals, flood and other overlays, legal access, body corporate records and your intended use. If you need an investigation the standard finance or building and pest conditions do not cover, discuss a specific due diligence condition before you sign.",
  },
  {
    q: "How do I avoid a dispute about fixtures and chattels?",
    a: "Make the contract's inclusions and exclusions clear before signing. Identify appliances, blinds, pool equipment, remotes and any other items you expect to stay; do not rely only on listing photographs or a verbal promise. At the pre-settlement inspection, compare the property and included items with the contract and tell your solicitor promptly if something is missing.",
  },
];

export const Route = createFileRoute("/buyers-solicitor-qld")({
  head: () => ({
    meta: [
      { title: fitTitle("Buyer's Solicitor QLD | Coastside Law") },
      {
        name: "description",
        content: fitDescription(
          "A solicitor for buying a house in Queensland. Free contract review before you sign, fixed fees, and a solicitor on your file from contract to settlement.",
        ),
      },
      { property: "og:title", content: "Buyer's solicitor in Queensland" },
      {
        property: "og:description",
        content: fitDescription("Free contract review, fixed fees and a real solicitor on your purchase. Coastside Law, Golden Beach and Scarborough."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/buyers-solicitor-qld" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/buyers-solicitor-qld" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: () => (
    <SituationPage
      eyebrow="Buying property"
      title="A buyer's solicitor for your Queensland purchase"
      lead="Buying a home is the biggest contract most people sign. We check it before you're locked in, then a solicitor runs your purchase through to settlement."
      boxTitle="What you get"
      boxItems={[
        "Free contract review before you sign",
        "A solicitor on your file, not an assistant",
        "Fixed fee, with searches and costs set out up front",
        "Finance, building and pest and cooling-off dates tracked for you",
        "Electronic settlement through PEXA",
      ]}
      faqs={FAQS}
      links={[
        { to: "/cooling-off-period-qld", label: "Cooling-off period in QLD" },
        { to: "/special-conditions-contract-qld", label: "Special conditions explained" },
        { to: "/fees", label: "Our fixed fees" },
        { to: "/conveyancing", label: "Conveyancing" },
        { to: "/form-2-buyer-rights-qld", label: "Form 2 and buyer rights" },
        { to: "/guides/due-diligence-clause-queensland", label: "Due diligence conditions" },
        { to: "/guides/title-search-queensland", label: "Title searches and encumbrances" },
        { to: "/guides/easements-in-queensland", label: "Easements and legal access" },
        { to: "/guides/joint-tenants-vs-tenants-in-common-queensland", label: "Choosing joint ownership" },
        { to: "/guides/pre-settlement-inspection-checklist-queensland", label: "Fixtures, chattels and your final inspection" },
      ]}
      formHeading="Send us your contract for a free review"
      ctaTitle="Buying? Talk to a solicitor first"
      ctaBody={`Call ${FIRM.phone} and we'll look over your contract before you sign.`}
    />
  ),
});
